/**
 * Árbol de estructura del modelo.
 *
 * Estado del árbol que dibujan `MostrarEstructura` y `NodoComponente`. La
 * estructura pertenece al MODELO y la comparten todas sus versiones, así que
 * editarla afecta a todas: solo se permite con el modelo en `edicion`.
 *
 * Los pesos se autodistribuyen entre hermanos mientras `auto_distribucion` esté
 * activo; fijar un peso a mano lo desactiva para ese grupo.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import Componente from '@/interfaces/Componente'
import { getTodosComponentesService, getComponentePorIdService, crearComponenteRaizService, crearHijoComponenteService, actualizarComponenteService, eliminarComponenteService, eliminarHijoComponenteService, reasignarPesosComponenteService, getComponenteConHijosService, reordenarComponenteService } from '@/services/ComponenteService'
import { http } from '@/services/http'

// Normaliza un nodo de componente
function normalizeNode(n: any, id_modelo_fallback?: number): Componente {
  const rawChildren = Array.isArray(n?.children)
    ? n.children
    : Array.isArray(n?.hijos)
      ? n.hijos
      : Array.isArray(n?.hijos_recursivos)
        ? n.hijos_recursivos
        : []

  const node: Componente = {
    id_componente: n.id_componente ?? n.id,
    id_modelo: n.id_modelo ?? id_modelo_fallback ?? undefined,
    ordinal: String(n.ordinal ?? '').trim(),
    nombre: n.nombre ?? '',
    peso: n.peso != null ? Number(n.peso) : null,
    descripcion: n.descripcion ?? null,
    padre_id: n.padre_id ?? null,
    es_indicador: n.es_indicador ?? undefined,
    enfoque: n.enfoque ?? null,
    grupo_variante: n.grupo_variante ?? null,
    children: []
  }

  node.children = rawChildren.map((h: any) =>
    normalizeNode(h, node.id_modelo ?? id_modelo_fallback)
  )

  return node
}

function normalizeForest(nodes: any[] | null | undefined, id_modelo_fallback?: number): Componente[] {
  return Array.isArray(nodes) ? nodes.map(n => normalizeNode(n, id_modelo_fallback)) : []
}

function asignarNiveles(nodo: Componente, niveles: string[], nivelActual = 0) {
  (nodo as any)._nivelNombre = niveles[nivelActual] ?? null
  if (nodo.children) {
    nodo.children.forEach((hijo: Componente) => asignarNiveles(hijo, niveles, nivelActual + 1))
  }
}

/** Store `componentes`. Ver la cabecera del archivo para el contrato del módulo. */
export const useComponentes = defineStore('componentes', {
  state: () => ({
    componentes: [] as Componente[],
    componenteActual: null as Componente | null,
    urlComponente: `${process.env.VUE_APP_API_BASE_URL}/api/componentes`,
    urlIndicadores: `${process.env.VUE_APP_API_BASE_URL}/api/indicadores`
  }),

  actions: {
    // Traer el árbol de componentes filtrando por versión (compat transicional)
    async cargarRaizPorVersion(id_version: number): Promise<Componente | null> {
      try {
        const url = `${this.urlComponente}?padre_id=null&id_version=${id_version}`
        const result = await http.get<any>(url)
        if (!result.ok) {
          console.error('Error al obtener raíz:', result.status)
          return null
        }
        const data = result.data

        const raizData = Array.isArray(data)
          ? data.find((c: any) => c.id_version === id_version)
          : null

        if (!raizData) {
          console.warn(`No se encontró raíz para la versión ${id_version}`)
          return null
        }

        return normalizeNode(raizData, raizData.id_modelo)
      } catch (e) {
        console.error('Error en cargarRaizPorVersion:', e)
        return null
      }
    },

    // Traer raíz de un modelo por id_modelo 
    async cargarRaizPorModelo(id_modelo: number): Promise<Componente | null> {
      try {
        const url = `${this.urlComponente}?padre_id=null&id_modelo=${id_modelo}`
        const result = await http.get<any>(url)
        if (!result.ok) {
          console.error('Error al obtener raíz por modelo:', result.status)
          return null
        }
        const data = result.data
        const raizData = Array.isArray(data)
          ? data.find((c: any) => !c.padre_id && c.id_modelo === id_modelo)
          : (data?.id_componente ? data : null)
        if (!raizData) {
          console.warn(`No se encontró raíz para el modelo ${id_modelo}`)
          return null
        }
        return normalizeNode(raizData, id_modelo)
      } catch (e) {
        console.error('Error en cargarRaizPorModelo:', e)
        return null
      }
    },

    // Traer todo el árbol desde un id de raíz — UNA SOLA REQUEST
    async cargarArbolDesdeRaiz(id_raiz: number, niveles?: string[]): Promise<boolean> {
      try {
        const arbolCompleto = await getComponenteConHijosService(this.urlComponente, id_raiz)
        if (!arbolCompleto) {
          console.warn('No se encontró el componente raíz')
          this.componentes = []
          return false
        }

        const raizNode = normalizeNode(arbolCompleto, arbolCompleto.id_modelo)

        if (niveles && niveles.length > 0) {
          asignarNiveles(raizNode, niveles)
        }

        this.componentes = [raizNode]
        return true
      } catch (e) {
        console.error('Error en cargarArbolDesdeRaiz:', e)
        this.componentes = []
        return false
      }
    },

    contarNodos(nodo: Componente): number {
      const hijos = nodo.children ?? []
      return 1 + hijos.reduce((acc: number, h: Componente) => acc + this.contarNodos(h), 0)
    },

    async cargarTodos(): Promise<void> {
      const lista = await getTodosComponentesService(this.urlComponente)
      this.componentes = normalizeForest(lista ?? [])
    },

    async cargarPorId(id_componente: number): Promise<void> {
      const comp = await getComponentePorIdService(this.urlComponente, id_componente)
      this.componenteActual = comp ? normalizeNode(comp) : null
    },

    // Crear raíz — Re-arquitectura: se envía id_modelo (canónico)
    async agregarRaiz(data: {
      id_modelo: number
      nombre: string
      peso?: number | null
      descripcion?: string | null
    }): Promise<Componente | null> {
      try {
        const nuevo = await crearComponenteRaizService(this.urlComponente, {
          id_modelo: data.id_modelo,
          nombre: data.nombre.trim(),
          peso: data.peso ?? 100,
          descripcion: data.descripcion ?? null
        })
        if (!nuevo) return null

        const lista = await getTodosComponentesService(this.urlComponente)
        this.componentes = normalizeForest(lista ?? [])

        return normalizeNode(nuevo, data.id_modelo)
      } catch (error) {
        console.error('[Store] Error en agregarRaiz:', error)
        return null
      }
    },

    // Crear hijo de un componente
    async agregarHijo(
      padreId: number | Componente,
      data: { nombre: string; peso?: number | null; descripcion?: string | null; enfoque?: string | null; grupo_variante?: string | null }
    ): Promise<{ data: Componente | null; error?: string }> {
      try {
        const idNum = typeof padreId === 'object'
          ? Number(padreId.id_componente)
          : Number(padreId)

        if (!idNum || Number.isNaN(idNum)) {
          console.error('[Store] padreId inválido:', padreId)
          return { data: null, error: 'ID de componente padre inválido' }
        }

        const result = await crearHijoComponenteService(this.urlComponente, idNum, {
          nombre: data.nombre.trim(),
          peso: data.peso != null ? Number(data.peso) : null,
          descripcion: data.descripcion ?? null,
          enfoque: data.enfoque ?? 'general',
          grupo_variante: data.grupo_variante ?? null
        })
        if (!result.data) return { data: null, error: result.error }

        const padreActualizado = await getComponenteConHijosService(this.urlComponente, idNum)

        const actualizarPadre = (lista?: Componente[]): boolean => {
          if (!lista) return false
          for (let i = 0; i < lista.length; i++) {
            if (lista[i].id_componente === idNum) {
              lista[i] = normalizeNode(padreActualizado)
              return true
            }
            if (actualizarPadre(lista[i].children)) return true
          }
          return false
        }

        actualizarPadre(this.componentes)
        this.componentes = [...this.componentes]

        return { data: normalizeNode(result.data) }
      } catch (error) {
        console.error('[Store] Error en agregarHijo:', error)
        return { data: null, error: 'Error inesperado al agregar componente' }
      }
    },

    // Actualizar componente
    async actualizar(comp: Componente): Promise<Componente | null> {
      const payload = {
        nombre: comp.nombre.trim(),
        peso: comp.peso != null ? Number(comp.peso) : null,
        descripcion: comp.descripcion ?? null,
        padre_id: comp.padre_id ?? null,
        id_tipo_componente: comp.id_tipo_componente ?? null,
        enfoque: comp.enfoque ?? 'general',
        grupo_variante: comp.grupo_variante ?? null
      }

      const actualizado = await actualizarComponenteService(
        this.urlComponente,
        comp.id_componente!,
        payload
      )

      if (!actualizado) return null

      try {
        const node = normalizeNode(actualizado, comp.id_modelo)

        if (!node.id_componente) {
          node.id_componente = comp.id_componente
        }

        const reemplazar = (lista?: Componente[]): boolean => {
          if (!lista) return false
          for (let i = 0; i < lista.length; i++) {
            if (lista[i].id_componente === node.id_componente) {
              node.children = node.children?.length
                ? node.children
                : (lista[i].children ?? [])
              lista.splice(i, 1, node)
              return true
            }
            if (reemplazar(lista[i].children)) return true
          }
          return false
        }

        reemplazar(this.componentes)
        this.componentes = [...this.componentes]
        return node
      } catch (error) {
        console.error('Error en actualizar componente:', error)
        return null
      }
    },

    // Eliminar componente (y sus descendientes)
    async eliminar(id_componente: number): Promise<boolean> {
      try {
        const ok = await eliminarComponenteService(this.urlComponente, id_componente)
        if (!ok) return false

        const eliminarDe = (lista?: Componente[]): boolean => {
          if (!lista) return false
          const idx = lista.findIndex(c => c.id_componente === id_componente)
          if (idx !== -1) {
            lista.splice(idx, 1)
            return true
          }
          for (const c of lista) {
            if (eliminarDe(c.children)) return true
          }
          return false
        }
        eliminarDe(this.componentes)
        return true
      } catch (error) {
        console.error('[Store] Error al eliminar componente:', error)
        return false
      }
    },

    // Eliminar hijo de un componente
    async eliminarHijo(padreId: number, hijoId: number): Promise<boolean> {
      try {
        const ok = await eliminarHijoComponenteService(this.urlComponente, padreId, hijoId)
        if (!ok) return false

        const eliminarDe = (lista?: Componente[]): boolean => {
          if (!lista) return false
          for (const comp of lista) {
            if (comp.id_componente === padreId && comp.children) {
              const idx = comp.children.findIndex(h => h.id_componente === hijoId)
              if (idx !== -1) {
                comp.children.splice(idx, 1)
                return true
              }
            }
            if (eliminarDe(comp.children)) return true
          }
          return false
        }
        eliminarDe(this.componentes)
        return true
      } catch (error) {
        console.error('[Store] Error al eliminar hijo:', error)
        return false
      }
    },

    // Reposicionar ordinal de un componente entre sus hermanos 
    async reordenar(id_componente: number, posicion: number): Promise<{ ok: boolean; message?: string }> {
      const result = await reordenarComponenteService(this.urlComponente, id_componente, posicion)
      if (result.ok && result.data) {
        const actualizado = normalizeNode(result.data)
        const reemplazar = (lista?: Componente[]): boolean => {
          if (!lista) return false
          for (let i = 0; i < lista.length; i++) {
            if (lista[i].id_componente === id_componente) {
              actualizado.children = lista[i].children
              lista.splice(i, 1, actualizado)
              return true
            }
            if (reemplazar(lista[i].children)) return true
          }
          return false
        }
        reemplazar(this.componentes)
        this.componentes = [...this.componentes]
      }
      return { ok: result.ok, message: result.message }
    },

    // Redistribuir pesos entre hermanos 
    async redistribuirPesosEntreHermanos(
      padreId: number,
      _hijoIdModificado?: number,
      _nuevoPesoRelativo?: number
    ): Promise<boolean> {
      try {
        const padre = this.componentes.find(c => c.id_componente === padreId)
        if (!padre || !padre.children) return false

        const pesos = padre.children.reduce((acc, h) => {
          acc[h.id_componente!] = h.peso ?? 0
          return acc
        }, {} as Record<number, number>)

        const response = await reasignarPesosComponenteService(this.urlComponente, padreId, pesos)
        if (!response) return false

        padre.children = response.hijos
        return true
      } catch (error) {
        console.error('[Store] Error al redistribuir pesos:', error)
        return false
      }
    }
  },
})
